import { useEffect, useMemo } from "react";
import {
  AdditiveBlending,
  CanvasTexture,
  SpriteMaterial,
  SRGBColorSpace,
} from "three";
import { LabelSprite } from "./LabelSprite";
import { SUN_POSITION, SUN_RADIUS } from "./cameraComposition";

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Procedural fiery disk: turbulent cells, limb darkening, soft circular edge.
// Rendered as a camera-facing sprite so perspective never stretches it.
function createSunDiskMaterial(): SpriteMaterial {
  const size = 512;
  const centre = size / 2;
  const radius = centre - 6;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");

  if (context === null) {
    throw new Error("Unable to create the sun disk texture");
  }

  context.fillStyle = "#7e1400";
  context.fillRect(0, 0, size, size);

  const random = mulberry32(20260821);
  const palette = [
    { color: "255, 214, 92", weight: 0.22 },
    { color: "255, 158, 42", weight: 0.34 },
    { color: "224, 74, 8", weight: 0.26 },
    { color: "116, 12, 0", weight: 0.18 },
  ];

  const pick = () => {
    let roll = random();
    for (const entry of palette) {
      roll -= entry.weight;
      if (roll <= 0) return entry.color;
    }
    return palette[0]?.color ?? "255, 214, 92";
  };

  const octaves = [
    { count: 60, minR: 40, maxR: 100, alpha: 0.5 },
    { count: 260, minR: 12, maxR: 36, alpha: 0.45 },
    { count: 900, minR: 2.5, maxR: 9, alpha: 0.4 },
  ];

  for (const { count, minR, maxR, alpha } of octaves) {
    for (let i = 0; i < count; i += 1) {
      const x = random() * size;
      const y = random() * size;
      const blobRadius = minR + random() * (maxR - minR);
      const color = pick();
      const gradient = context.createRadialGradient(x, y, 0, x, y, blobRadius);
      gradient.addColorStop(0, `rgba(${color}, ${alpha})`);
      gradient.addColorStop(1, `rgba(${color}, 0)`);
      context.fillStyle = gradient;
      context.fillRect(
        x - blobRadius,
        y - blobRadius,
        blobRadius * 2,
        blobRadius * 2,
      );
    }
  }

  // Limb darkening toward the rim
  const limb = context.createRadialGradient(
    centre,
    centre,
    radius * 0.55,
    centre,
    centre,
    radius,
  );
  limb.addColorStop(0, "rgba(60, 4, 0, 0)");
  limb.addColorStop(1, "rgba(60, 4, 0, 0.55)");
  context.fillStyle = limb;
  context.fillRect(0, 0, size, size);

  // Keep only the circular disk, with a softly feathered edge
  const mask = context.createRadialGradient(
    centre,
    centre,
    0,
    centre,
    centre,
    radius,
  );
  mask.addColorStop(0, "rgba(255, 255, 255, 1)");
  mask.addColorStop(0.965, "rgba(255, 255, 255, 1)");
  mask.addColorStop(1, "rgba(255, 255, 255, 0)");
  context.globalCompositeOperation = "destination-in";
  context.fillStyle = mask;
  context.fillRect(0, 0, size, size);
  context.globalCompositeOperation = "source-over";

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;

  return new SpriteMaterial({
    depthWrite: false,
    map: texture,
    toneMapped: false,
    transparent: true,
  });
}

function createGlowMaterial(opacity: number): SpriteMaterial {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");

  if (context === null) {
    throw new Error("Unable to create the sun glow texture");
  }

  // Hot orange corona hugging the disk
  const gradient = context.createRadialGradient(128, 128, 0, 128, 128, 128);
  gradient.addColorStop(0, "rgba(255, 190, 90, 0.85)");
  gradient.addColorStop(0.35, "rgba(255, 130, 40, 0.4)");
  gradient.addColorStop(0.65, "rgba(230, 70, 15, 0.14)");
  gradient.addColorStop(1, "rgba(200, 50, 10, 0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 256, 256);

  return new SpriteMaterial({
    blending: AdditiveBlending,
    depthWrite: false,
    map: new CanvasTexture(canvas),
    opacity,
    toneMapped: false,
    transparent: true,
  });
}

export function Sun({
  classroomMode,
  position = SUN_POSITION,
  radius = SUN_RADIUS,
}: {
  readonly classroomMode: boolean;
  readonly position?: readonly [number, number, number];
  readonly radius?: number;
}) {
  const disk = useMemo(() => createSunDiskMaterial(), []);
  const coreGlow = useMemo(() => createGlowMaterial(1), []);
  const haloGlow = useMemo(() => createGlowMaterial(0.45), []);

  useEffect(
    () => () => {
      for (const material of [disk, coreGlow, haloGlow]) {
        material.map?.dispose();
        material.dispose();
      }
    },
    [disk, coreGlow, haloGlow],
  );

  return (
    <group position={position}>
      <sprite material={disk} scale={[radius * 2, radius * 2, 1]} />
      <sprite material={coreGlow} scale={[radius * 2.3, radius * 2.3, 1]} />
      <sprite material={haloGlow} scale={[radius * 3.2, radius * 3.2, 1]} />
      <LabelSprite
        text={classroomMode ? "Sun — the lamp" : "Sun"}
        position={[0, -radius - 1.2, 0]}
        scale={classroomMode ? [3.4, 0.6, 1] : [1.8, 0.6, 1]}
        color="#ffe3a3"
      />
    </group>
  );
}
